import chroma from 'chroma-js';
import { LayerProps } from 'shared/map-exports';
import {
  MapLayer,
  SATELLITE_NAMES,
  SATELLITES_BY_LAYER,
} from 'features/mapping/mapping.constants';
import { SOURCE_LAYER } from './HeatDetectionsLayer.constants';

// Collect the satellite names surfaced by every currently-visible hotspot
// layer, so the heat-detections style can filter to just those satellites.
export const getEnabledSatellites = (
  isLayerVisible: (layer: MapLayer) => boolean,
): string[] =>
  Object.entries(SATELLITES_BY_LAYER)
    .filter(([layer]) => isLayerVisible(layer as MapLayer))
    .flatMap(([, satellites]) => satellites);

// Red-to-orange gradient for hours since acquisition
const colorStops0to24 = (() => {
  const scale = chroma
    .scale(['red', 'orange'])
    .domain([0, 24])
    .mode('lab')
    .correctLightness();
  const stops: (number | string)[] = [];
  for (let i = 0; i <= 24; i += 1) stops.push(i, scale(i).hex());
  return stops;
})();

const SECONDS_PER_HOUR = 3600;

// z22 endpoint radii (px). Landsat ≈ true 30m @40°N: 30/2/((156543.03392/2**22)*cos(40°)) ≈ 525.
// VIIRS/MODIS = legacy ArcGIS (ViirsLayer/ModisLayer) radii.
const LANDSAT_Z22_RADIUS = 1500;
const VIIRS_Z22_RADIUS = 20000;
const MODIS_Z22_RADIUS = 60000;

// Minimum on-screen radius (px), used as the z0 endpoint (matches the legacy
// layers), so isolated detections stay visible at low zoom. The ramp only
// increases from here, so this is the floor across all zooms.
const MIN_RADIUS_PX = 2;

export const getCircleStyle = (enabledSatellites: string[]): LayerProps => {
  // Inject the current epoch-seconds so the expression can derive
  // "hours since acquisition" from the tile's acquisition_epoch field.
  const nowEpochSeconds = Math.floor(Date.now() / 1000);

  // acquisition_epoch is Unix epoch-seconds (see the heat_detections_gis_24h
  // view in watchduty-backend); compute hours elapsed since acquisition.
  const hoursSinceAcquisition = [
    '/',
    ['-', nowEpochSeconds, ['to-number', ['get', 'acquisition_epoch']]],
    SECONDS_PER_HOUR,
  ];

  const clampedHours = ['min', 24, ['max', 0, hoursSinceAcquisition]];
  const fadeOpacityOverTime = [
    'interpolate',
    ['linear'],
    clampedHours,
    0,
    1.0,
    24,
    0.6,
  ];

  return {
    type: 'circle',
    'source-layer': SOURCE_LAYER,
    filter: [
      'all',
      ['>=', hoursSinceAcquisition, 0],
      ['<=', hoursSinceAcquisition, 24],
      ['in', ['get', 'satellite'], ['literal', enabledSatellites]],
    ],
    layout: {
      // Landsat on top of VIIRS/MODIS, then sort by acquisition time.
      'circle-sort-key': [
        '+',
        [
          'match',
          ['get', 'satellite'],
          SATELLITE_NAMES.LANDSAT,
          1_000_000_000,
          0,
        ],
        ['to-number', ['get', 'acquisition_epoch']],
      ],
    },
    paint: {
      // Landsat holds its z13 opacity at higher zooms (its circles are near
      // true 30m size, so the high-zoom fade-out only washes it out)
      'circle-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        3,
        ['*', 0.35, fadeOpacityOverTime],
        8,
        ['*', 0.5, fadeOpacityOverTime],
        13,
        ['*', 0.4, fadeOpacityOverTime],
        14,
        [
          '*',
          // Landsat stays visible through the full zoom range.
          ['match', ['get', 'satellite'], SATELLITE_NAMES.LANDSAT, 0.4, 0.07],
          fadeOpacityOverTime,
        ],
      ],
      'circle-blur': 0.5,
      // z0 = shared visibility floor; z22 per-sensor (see *_Z22_RADIUS). VIIRS is the default.
      'circle-radius': [
        'interpolate',
        ['exponential', 2],
        ['zoom'],
        0,
        MIN_RADIUS_PX,
        22,
        [
          'match',
          ['get', 'satellite'],
          SATELLITE_NAMES.MODIS,
          MODIS_Z22_RADIUS,
          SATELLITE_NAMES.LANDSAT,
          LANDSAT_Z22_RADIUS,
          VIIRS_Z22_RADIUS,
        ],
      ],
      'circle-color': [
        'interpolate',
        ['linear'],
        clampedHours,
        ...colorStops0to24,
      ],
    },
  };
};
